// src/components/vehicles/VehicleStatistics.js
import React, { useState, useEffect, useContext } from "react";
import { Dropdown } from "primereact/dropdown";
import { Calendar } from "primereact/calendar";
import { ProgressSpinner } from "primereact/progressspinner";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend, AreaChart, Area, Line,
} from "recharts";
import { api, formatCurrency, toLocalDateString } from "../../utils/api";
import { AuthContext } from "../../context/AuthContext";
import "../../css/Fleet.css";

const COLORS = ["#3b82f6", "#ef4444", "#f59e0b", "#10b981", "#8b5cf6", "#ec4899", "#06b6d4", "#f97316", "#84cc16", "#6366f1"];

const VehicleStatistics = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [vehicles, setVehicles] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [dateFrom, setDateFrom] = useState(null);
  const [dateTo, setDateTo] = useState(null);
  const { user } = useContext(AuthContext);
  const isAdmin = user?.role === "Administrator";

  useEffect(() => { loadDictionaries(); }, []);
  useEffect(() => { loadStats(); }, [selectedVehicle, selectedCompany, dateFrom, dateTo]);

  const loadDictionaries = async () => {
    try {
      const [vRes, cRes] = await Promise.all([api.get("/vehicles", { params: { limit: 1000 } }), api.get("/vehicle-companies")]);
      setVehicles((vRes.data?.data || []).map((v) => ({ ...v, label: `${v.brand} ${v.model} (${v.registration_number})` })));
      setCompanies(Array.isArray(cRes.data) ? cRes.data : []);
    } catch (err) { console.error(err); }
  };

  const loadStats = async () => {
    setLoading(true);
    try {
      const params = {};
      if (selectedVehicle) params.vehicleId = selectedVehicle;
      if (selectedCompany) params.companyId = selectedCompany;
      if (dateFrom) params.dateFrom = toLocalDateString(dateFrom);
      if (dateTo) params.dateTo = toLocalDateString(dateTo);
      const res = await api.get("/repair-statistics", { params });
      setStats(res.data);
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  const currencyFormatter = (value) =>
    new Intl.NumberFormat("pl-PL", { style: "currency", currency: "PLN" }).format(value);

  const customTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="fleet-tooltip">
          <p style={{ margin: "0 0 0.25rem", fontWeight: 600, fontSize: "0.85rem" }}>{label}</p>
          {payload.map((p, i) => (
            <p key={i} style={{ margin: 0, color: p.color, fontSize: "0.8rem" }}>
              {p.name}: {p.name === "Koszt" ? currencyFormatter(p.value) : p.value}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  const EmptyChart = () => (
    <div className="fleet-chart-empty"><i className="pi pi-chart-bar" style={{ fontSize: "2rem", opacity: 0.3 }} /><p>Brak danych do wyświetlenia</p></div>
  );

  // Compute KPIs from stats
  const totalCost = stats?.byCategory?.reduce((s, c) => s + (parseFloat(c.totalCost) || 0), 0) || 0;
  const totalCount = stats?.byCategory?.reduce((s, c) => s + (c.count || 0), 0) || 0;
  const avgCostPerRepair = totalCount > 0 ? totalCost / totalCount : 0;
  const topCategory = stats?.byCategory?.[0];
  const topVehicle = stats?.byVehicle?.[0];

  return (
    <div className="fleet-page fleet-animate-in">
      {/* Header */}
      <div className="fleet-page-header">
        <div>
          <h1><span className="fleet-header-icon">📊</span>Statystyki napraw</h1>
          <p className="fleet-page-subtitle">Analiza kosztów i trendów napraw floty</p>
        </div>
      </div>

      {/* KPI Summary Cards */}
      {stats && !loading && isAdmin && (
        <div className="fleet-stats-grid fleet-stats-grid--4">
          <div className="fleet-stat-card fleet-stat-card--primary">
            <div className="fleet-stat-icon fleet-stat-icon--primary"><i className="pi pi-wrench" /></div>
            <div className="fleet-stat-value fleet-stat-value--primary">{totalCount}</div>
            <div className="fleet-stat-label">Napraw ogółem</div>
            <div className="fleet-stat-sub">{stats.byVehicle?.length || 0} pojazdów</div>
          </div>

          <div className="fleet-stat-card fleet-stat-card--danger">
            <div className="fleet-stat-icon fleet-stat-icon--danger"><i className="pi pi-wallet" /></div>
            <div className="fleet-stat-value fleet-stat-value--danger" style={{ fontSize: "1.2rem" }}>{formatCurrency(totalCost)}</div>
            <div className="fleet-stat-label">Łączny koszt</div>
            <div className="fleet-stat-sub">śr. {formatCurrency(avgCostPerRepair)}</div>
          </div>

          <div className="fleet-stat-card fleet-stat-card--warning">
            <div className="fleet-stat-icon fleet-stat-icon--warning"><i className="pi pi-tag" /></div>
            <div className="fleet-stat-value fleet-stat-value--warning" style={{ fontSize: "0.95rem" }}>{topCategory?.name || "—"}</div>
            <div className="fleet-stat-label">Najdroższa kategoria</div>
            {topCategory && <div className="fleet-stat-sub">{formatCurrency(topCategory.totalCost)}</div>}
          </div>

          <div className="fleet-stat-card fleet-stat-card--purple">
            <div className="fleet-stat-icon fleet-stat-icon--purple"><i className="pi pi-car" /></div>
            <div className="fleet-stat-value fleet-stat-value--purple" style={{ fontSize: "0.85rem" }}>{topVehicle?.name?.split('(')[0]?.trim() || "—"}</div>
            <div className="fleet-stat-label">Najdroższy pojazd</div>
            {topVehicle && <div className="fleet-stat-sub">{formatCurrency(topVehicle.totalCost)}</div>}
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="fleet-filters">
        <Dropdown value={selectedVehicle} options={vehicles} onChange={(e) => setSelectedVehicle(e.value)} optionLabel="label" optionValue="id" placeholder="Pojazd" showClear filter className="fleet-filter-vehicle" />
        <Dropdown value={selectedCompany} options={companies} onChange={(e) => setSelectedCompany(e.value)} optionLabel="name" optionValue="id" placeholder="Firma" showClear />
        <Calendar value={dateFrom} onChange={(e) => setDateFrom(e.value)} placeholder="Od daty" dateFormat="dd.mm.yy" showIcon showButtonBar />
        <Calendar value={dateTo} onChange={(e) => setDateTo(e.value)} placeholder="Do daty" dateFormat="dd.mm.yy" showIcon showButtonBar />
      </div>

      {loading ? (
        <div className="fleet-loading"><ProgressSpinner /></div>
      ) : stats ? (
        <div className="fleet-charts-grid">
          {/* By category */}
          <div className="fleet-chart-card">
            <div className="fleet-chart-title"><i className="pi pi-chart-bar" /> Koszty wg kategorii</div>
            {stats?.byCategory?.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={stats.byCategory}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--fleet-border-light)" />
                  <XAxis dataKey="name" tick={{ fontSize: 12, fill: "var(--fleet-text-secondary)" }} />
                  <YAxis tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} tick={{ fontSize: 12, fill: "var(--fleet-text-muted)" }} />
                  <Tooltip content={customTooltip} />
                  <Bar dataKey="totalCost" name="Koszt" fill="var(--fleet-primary)" radius={[6, 6, 0, 0]} />
                  <Bar dataKey="count" name="Ilość" fill="var(--fleet-success)" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : <EmptyChart />}
          </div>

          {/* By vehicle */}
          <div className="fleet-chart-card">
            <div className="fleet-chart-title"><i className="pi pi-car" /> Koszty wg pojazdu (TOP 10)</div>
            {stats?.byVehicle?.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={stats.byVehicle.slice(0, 10)} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--fleet-border-light)" />
                  <XAxis type="number" tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} tick={{ fontSize: 12, fill: "var(--fleet-text-muted)" }} />
                  <YAxis type="category" dataKey="name" width={200} tick={{ fontSize: 11, fill: "var(--fleet-text-secondary)" }} />
                  <Tooltip content={customTooltip} />
                  <Bar dataKey="totalCost" name="Koszt" fill="var(--fleet-purple)" radius={[0, 6, 6, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : <EmptyChart />}
          </div>

          {/* By company */}
          <div className="fleet-chart-card">
            <div className="fleet-chart-title"><i className="pi pi-building" /> Koszty wg firmy</div>
            {stats?.byCompany?.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie data={stats.byCompany} dataKey="totalCost" nameKey="name" cx="50%" cy="50%" outerRadius={100} innerRadius={40}
                    label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`} labelLine={{ stroke: "var(--fleet-text-muted)" }}>
                    {(stats?.byCompany || []).map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                  </Pie>
                  <Tooltip formatter={(val) => currencyFormatter(val)} />
                  <Legend wrapperStyle={{ fontSize: "0.8rem" }} />
                </PieChart>
              </ResponsiveContainer>
            ) : <EmptyChart />}
          </div>

          {/* Monthly trend — area chart */}
          <div className="fleet-chart-card">
            <div className="fleet-chart-title"><i className="pi pi-chart-line" /> Trend miesięczny</div>
            {stats?.byMonth?.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <AreaChart data={stats.byMonth}>
                  <defs>
                    <linearGradient id="costGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="var(--fleet-primary)" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="var(--fleet-primary)" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--fleet-border-light)" />
                  <XAxis dataKey="month" tick={{ fontSize: 12, fill: "var(--fleet-text-secondary)" }} />
                  <YAxis yAxisId="cost" tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} tick={{ fontSize: 12, fill: "var(--fleet-text-muted)" }} />
                  <YAxis yAxisId="count" orientation="right" tick={{ fontSize: 12, fill: "var(--fleet-text-muted)" }} />
                  <Tooltip content={customTooltip} />
                  <Area yAxisId="cost" type="monotone" dataKey="totalCost" name="Koszt" stroke="var(--fleet-primary)" strokeWidth={2.5} fill="url(#costGrad)" />
                  <Line yAxisId="count" type="monotone" dataKey="count" name="Ilość" stroke="var(--fleet-success)" strokeWidth={2.5} dot={{ r: 4, fill: "var(--fleet-success)" }} activeDot={{ r: 6 }} />
                  <Legend wrapperStyle={{ fontSize: "0.8rem" }} />
                </AreaChart>
              </ResponsiveContainer>
            ) : <EmptyChart />}
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default VehicleStatistics;
